import { Container, Graphics, Rectangle } from 'pixi.js';
import InteractionDecodeIconButton from "@/components/InteractionDecodeIconButton.ts";
import ICreate from "@/interfaces/ICreate.ts";
import IDestroy from "@/interfaces/IDestroy.ts";
import MarkText from "@/components/MarkText.ts";
import Placement from "@/types/Placement.ts";
import gsap from 'gsap';

export default class InteractionDecodeIconButtonGroup extends Container implements ICreate, IDestroy
{
    public static RIGHT:number = 1;
    public static LEFT:number = -1;
    
    public btns: InteractionDecodeIconButton[];
    public hasDoubleSelection:boolean;
    public hasTwoButtons:boolean;
    public iconString:string;
    public id:number;       // This is the index of the button in the array with all the buttons
    public indices:number[];
    public index:number;    // This is the index of the button in the group (placement (top or bottom))
    public isPartOfTrigraphDGE:boolean;
    public secondBtnShown:boolean;
    public secondBtnWidth:number;
    public startsTrigraphDGE:boolean;
    public targetTextField:MarkText;

    private _btnHeight:number;
    private _btnWidth:number;
    private _code:string;
    private _direction:number;
    private _placement:string;
    private _positionsMerged:number;
    private _usable:boolean;
    private curBtnIndex:number;
    private hitShape:Graphics;
    private layer1:Container;
    private layer2:Container;
    
    public get btnHeight():number
    {
        return this._btnHeight;
    }
    
    public set btnHeight(value:number)
    {
        this._btnHeight = value;
    }
    
    public get btnWidth():number
    {
        return this._btnWidth;
    }
    
    public set btnWidth(value:number)
    {
        this._btnWidth = value;
    }
    
    public get charPosition():number
    {
        return this.index + 1;
    }
    
    public get direction():number
    {
        return this._direction;
    }
    
    public get markCode():string
    {
        return this._code;
    }
    
    public set markCode(value:string)
    {
        this._code = value;
    }
    
    public get placement():string
    {
        return this._placement;
    }
    
    public set placement(value:string)
    {
        this._placement = value;
        if(this._placement == Placement.BTM)
        {
            this._direction = InteractionDecodeIconButtonGroup.RIGHT;
        }
        else
        {
            this._direction = InteractionDecodeIconButtonGroup.LEFT;
        }
    }
    
    public get positionsMerged()
    {
        return this._positionsMerged;
    }
    
    public get usable():boolean
    {
        return this._usable;
    }
    
    public set usable(value:boolean)
    {
        this._usable = value;
    }

    constructor()
    {
        super();
        this._init();
    }

    private _init(): void
    {
        this._positionsMerged = 0;
        this._usable = true;
        this.btns = [];
        this.curBtnIndex = 0;
        this._direction = InteractionDecodeIconButtonGroup.RIGHT;
        this.hasDoubleSelection = false;
        this.hasTwoButtons = false;
        this.isPartOfTrigraphDGE = false;
        this.secondBtnShown = false;
        this.startsTrigraphDGE = false;
        this.markCode = "";
        this.eventMode = "static";
        this.cursor = "pointer";
    }

    private createFirstButton():void
    {
        let btn:InteractionDecodeIconButton = new InteractionDecodeIconButton();
        btn.btnWidth = this._btnWidth;
        btn.btnHeight = this._btnHeight;
        btn.iconString = this.iconString;
        btn.placement = this._placement;
        btn.targetTextField = this.targetTextField;
        btn.create();
        this.layer1.addChild(btn);
        this.btns.push(btn);
    }
    
    private createHitArea(width:number, destroyFirst:boolean=false):void
    {
        if(destroyFirst)
        {
            this.destroyHitArea();
        }
        this.hitShape = new Graphics();
        this.hitShape.rect(0, 0, width, this._btnHeight);
        this.hitShape.fill(0x00FF00);
        this.hitShape.alpha = 0;
        this.layer2.addChild(this.hitShape);
        this.hitArea = new Rectangle(0, 0, width, this._btnHeight);
    }
    
    private createLayer1():void
    {
        this.layer1 = new Container();
        this.addChild(this.layer1);
    }
    
    private createLayer2():void
    {
        this.layer2 = new Container();
        this.addChild(this.layer2);
    }

    private destroyButtons():void
    {
        for(let i:number = 0; i < this.btns.length; i++)
        {
            this.btns[i].destroy();
        }
        this.btns = [];
    }
    
    private destroyHitArea():void
    {
        if(this.hitShape)
        {
            this.layer2.removeChild(this.hitShape);
            this.hitShape.destroy();
            this.hitShape = null;
        }
    }
    
    private destoryLayer1():void
    {
        if(this.layer1)
        {
            this.removeChild(this.layer1);
            this.layer1.removeChildren();
            this.layer1.destroy();
            this.layer1 = null;
        }
    }
    
    private destroyLayer2():void
    {
        if(this.layer2)
        {
            this.removeChild(this.layer2);
            this.layer2.removeChildren();
            this.layer2.destroy();
            this.layer2 = null;
        }
    }
    

    public create():void
    {
        this.createLayer1();
        this.createLayer2();
        this.createFirstButton();
        this.createHitArea(this._btnWidth);
    }

    public createSecondButton(positionsMerged:number):void
    {
        this._positionsMerged = positionsMerged;
        this.hasTwoButtons = true;
        let btn:InteractionDecodeIconButton = new InteractionDecodeIconButton();
        btn.btnWidth = this.secondBtnWidth;
        btn.btnHeight = this._btnHeight;
        btn.iconString = this.iconString;
        btn.placement = this._placement;
        btn.targetTextField = this.targetTextField;
        btn.create();
        btn.visible = false;
        this.layer1.addChild(btn);
        this.btns.push(btn);
    }
    
    public destroy():void
    {
        this.destroyButtons();
        this.destroyLayer2();
        this.destoryLayer1();
        super.destroy();
    }
    
    public disable():void
    {
        this.eventMode = null;
        this.cursor = null;
        this.btns[this.curBtnIndex].disable();
    }
    
    public enable():void
    {
        this.btns[this.curBtnIndex].enable();
        this.cursor = "pointer";
        this.eventMode = "static";
    }
    
    public hide():void
    {
        this.btns[this.curBtnIndex].hide();
    }

    public pointerOut():void
    {
        this.btns[this.curBtnIndex].pointerOut();
    }

    public pointerOver():void
    {
        this.btns[this.curBtnIndex].pointerOver();
    }
    
    public reset():void
    {
        this.btns[this.curBtnIndex].reset();
    }

    public showSecondButton():void
    {
        this.createHitArea(this.secondBtnWidth, true);
        this.btns[0].disable();
        this.curBtnIndex = 1;
        this.hitArea = new Rectangle(0, 0, this.secondBtnWidth, this.btns[0].btnHeight);
        this.btns[this.curBtnIndex].alpha = 0;
        this.btns[this.curBtnIndex].visible = true;
        gsap.to(this.btns[0], {duration:.75, alpha:0, ease:"power2.out"});
        gsap.to(this.btns[this.curBtnIndex], {duration:.75, alpha:1, ease:"power2.out", delay:.75});
    }
}